<template>
  <div class="ios-glass-page">
    <!-- 1. 背景光斑 -->
    <div class="background-blobs">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
    </div>

    <!-- 2. 顶部固定区域 (班级选择 + 状态筛选与排序) -->
    <div class="fixed-header">
      
      <!-- 班级选择器 -->
      <div class="class-selector glass-panel" @click="openClassPicker">
        <div class="selector-content">
          <span class="label">当前班级</span>
          <div class="value">
            {{ searchObj.className || '点击选择班级' }}
            <van-icon name="arrow-down" size="12" />
          </div>
        </div>
      </div>

      <!-- 控制栏：状态筛选与排序规则切换 (统一 iOS 毛玻璃分段胶囊) -->
      <div class="filter-sort-controls">
        <!-- 状态筛选 -->
        <div class="segment-control status-segment">
          <div class="segment-bg">
            <div 
              v-for="opt in computedStatusOptions" 
              :key="opt.value"
              class="segment-item"
              :class="{ active: searchObj.searchFaceStatus === opt.value }"
              @click="handleFilterChange(opt.value)"
            >
              {{ opt.text }}
            </div>
          </div>
        </div>

        <!-- 排序规则切换 (首字母排序 / 学号排序) -->
        <div class="segment-control sort-segment">
          <div class="segment-bg">
            <div 
              v-for="opt in sortOptions" 
              :key="opt.value"
              class="segment-item"
              :class="{ active: searchObj.sortType === opt.value }"
              @click="handleSortChange(opt.value)"
            >
              {{ opt.text }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 3. 列表区域 -->
    <div class="list-container">
      <div v-if="!searchObj.classCode" class="empty-state">
        <van-icon name="apps-o" size="48" color="rgba(0,0,0,0.2)" />
        <p>请先选择班级</p>
      </div>

      <div v-else-if="isListEmpty" class="empty-state">
        <van-icon name="search" size="48" color="rgba(0,0,0,0.2)" />
        <p>暂无学生数据</p>
      </div>

      <!-- 首字母排序：A-Z 索引分组列表 -->
      <van-index-bar 
        v-else-if="searchObj.sortType === 'pinyin'"
        :index-list="computedIndexList" 
        :sticky="false" 
        highlight-color="#007AFF"
        class="custom-index-bar"
      >
        <div v-for="(group, i) in indexList" :key="i">
          <van-index-anchor :index="group.title" class="glass-anchor" />
          
          <div 
            v-for="(student, ic) in group.datas" 
            :key="student.id || student.snCode || ic" 
            class="student-card glass-item"
            @click="onCardClick(student)"
          >
            <!-- 信息区 -->
            <div class="info">
              <div class="name-row">
                <span class="name">{{ student.studentName }}</span>
              </div>
              <div class="meta-row">
                <span class="label">学号</span>
                <span class="code">{{ student.snCode }}</span>
              </div>
            </div>

            <!-- 右侧：状态/操作区 -->
            <div class="action-area">
              <!-- 情况A：已制证 (显示结果 + 重置按钮) -->
              <div v-if="+student.faceStatus > 0" class="action-done-group">
                <div class="status-box done">
                  <div class="icon-circle">
                    <van-icon name="success" />
                  </div>
                  <span class="text">已制证</span>
                </div>
                <div class="reset-btn" @click.stop="handleReset(student)">
                  重置
                </div>
              </div>

              <!-- 情况B：未制证 (去制证按钮) -->
              <div v-else class="status-btn todo" @click.stop="goInfo(student)">
                <van-icon name="photograph" />
                <span>去制证</span>
              </div>
            </div>
          </div>
        </div>
      </van-index-bar>

      <!-- 学号排序：平铺列表 -->
      <div v-else-if="searchObj.sortType === 'snCode'" class="flat-list-wrapper">
        <div 
          v-for="(student, ic) in sortedFlatList" 
          :key="student.id || student.snCode || ic" 
          class="student-card glass-item"
          @click="onCardClick(student)"
        >
          <!-- 序号徽章 -->
          <div class="index-badge">{{ ic + 1 }}</div>

          <!-- 信息区 -->
          <div class="info">
            <div class="name-row">
              <span class="name">{{ student.studentName }}</span>
            </div>
            <div class="meta-row">
              <span class="label">学号</span>
              <span class="code">{{ student.snCode }}</span>
            </div>
          </div>

          <!-- 右侧：状态/操作区 -->
          <div class="action-area">
            <!-- 情况A：已制证 (显示结果 + 重置按钮) -->
            <div v-if="+student.faceStatus > 0" class="action-done-group">
              <div class="status-box done">
                <div class="icon-circle">
                  <van-icon name="success" />
                </div>
                <span class="text">已制证</span>
              </div>
              <div class="reset-btn" @click.stop="handleReset(student)">
                重置
              </div>
            </div>

            <!-- 情况B：未制证 (去制证按钮) -->
            <div v-else class="status-btn todo" @click.stop="goInfo(student)">
              <van-icon name="photograph" />
              <span>去制证</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部班级选择弹窗 (不绑定 @confirm，避免 confirm 内部触发递归爆栈) -->
    <van-popup 
      v-model="classShowPicker" 
      round 
      position="bottom" 
      class="glass-popup"
      @close="onCancelPicker"
    >
      <div class="popup-header">
        <div class="btn cancel" @click="onCancelPicker">取消</div>
        <div class="title">选择班级</div>
        <div class="btn confirm" @click="onConfirmPicker">确定</div>
      </div>

      <!-- 年级快捷胶囊栏 (横向滚动) -->
      <div v-if="gradeList && gradeList.length > 1" class="grade-capsule-bar">
        <div class="grade-scroll-wrap">
          <div 
            v-for="grade in gradeList" 
            :key="grade.code"
            class="grade-pill"
            :class="{ active: tempGrade === grade.code }"
            @click="handleGradePillClick(grade.code)"
          >
            <span class="grade-name">{{ grade.name }}</span>
            <span class="grade-count">({{ grade.count }})</span>
          </div>
        </div>
      </div>

      <!-- 班级滚轮 -->
      <van-picker 
        ref="classPicker"
        :columns="filteredClassList" 
        :default-index="currentClassIndex"
        :show-toolbar="false" 
        item-height="44"
        @change="onPickerChange"
      >
        <template #option="item">
          <div class="picker-option-row">
            <span class="picker-class-name">{{ item.text }}</span>
            <span 
              class="picker-count-badge" 
              :class="{ 'is-full': item.totalCount > 0 && item.doneCount >= item.totalCount }"
            >
              已制证 {{ item.doneCount || 0 }}/{{ item.totalCount || 0 }}
            </span>
          </div>
        </template>
      </van-picker>
    </van-popup>

  </div>
</template>

<script>
import { getClassInfoList, getStudentInfoList, resetStudentFace } from "@/api/teacher/teacherReporting.js";
import { setSession, getSession, removeSession } from "@/utils/cookie";
import { pinyin } from 'pinyin-pro';
import { Dialog } from 'vant';

export default {
  name: 'FaceRegistration',
  data() {
    return {
      allClassList: [],
      classShowPicker: false,
      tempGrade: 'ALL',
      dataList: [], // 原始数据
      indexList: [], // 拼音排序分组后的数据
      sortedFlatList: [], // 学号排序后的平铺数据
      
      searchObj: {
        classCode: '',
        className: '',
        studentName: '',
        selectedGrade: 'ALL', // 已确认生效的年级编码，默认 'ALL'
        searchFaceStatus: 'all', // 默认全部
        sortType: 'pinyin', // 默认首字母排序，可选 'pinyin' | 'snCode'
      },

      sortOptions: [
        { text: '首字母排序', value: 'pinyin' },
        { text: '学号排序', value: 'snCode' },
      ],
    };
  },
  computed: {
    // 当前班级实时统计人数
    currentStats() {
      const total = this.dataList ? this.dataList.length : 0;
      const done = this.dataList ? this.dataList.filter(item => +item.faceStatus > 0).length : 0;
      const todo = total - done;
      return { total, done, todo };
    },
    // 状态筛选分段选项（动态带人数）
    computedStatusOptions() {
      if (!this.searchObj.classCode) {
        return [
          { text: '全部', value: 'all' },
          { text: '未制证', value: 0 },
          { text: '已制证', value: 1 },
        ];
      }
      return [
        { text: `全部 (${this.currentStats.total})`, value: 'all' },
        { text: `未制证 (${this.currentStats.todo})`, value: 0 },
        { text: `已制证 (${this.currentStats.done})`, value: 1 },
      ];
    },
    // 年级聚合胶囊列表
    gradeList() {
      if (!this.allClassList || this.allClassList.length === 0) return [];
      const map = new Map();
      this.allClassList.forEach(item => {
        const key = item.gradeKey;
        const name = item.gradeName || '未分年级';
        if (!map.has(key)) {
          map.set(key, { code: key, name: name, count: 0 });
        }
        map.get(key).count++;
      });
      return [
        { code: 'ALL', name: '全部年级', count: this.allClassList.length },
        ...Array.from(map.values())
      ];
    },
    // 根据 tempGrade 过滤出的班级列表
    filteredClassList() {
      if (!this.tempGrade || this.tempGrade === 'ALL') {
        return this.allClassList;
      }
      return this.allClassList.filter(item => item.gradeKey === this.tempGrade);
    },
    // 提取索引字母列表给右侧导航用
    computedIndexList() {
      return this.indexList.map(item => item.title);
    },
    // 当前选中班级在 filteredClassList 中的索引 (增强双因子匹配 + 回退)
    currentClassIndex() {
      if (!this.searchObj.classCode || !this.filteredClassList.length) return 0;
      let idx = this.filteredClassList.findIndex(item => 
        item.value === this.searchObj.classCode && 
        (!this.searchObj.className || item.text === this.searchObj.className)
      );
      if (idx < 0) {
        idx = this.filteredClassList.findIndex(item => item.value === this.searchObj.classCode);
      }
      return idx >= 0 ? idx : 0;
    },
    // 列表是否为空
    isListEmpty() {
      if (this.searchObj.sortType === 'snCode') {
        return !this.sortedFlatList || this.sortedFlatList.length === 0;
      }
      return !this.indexList || this.indexList.length === 0;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!to || !to.path || to.path.indexOf('/teacherAddFaceIn/addFaceInfo') === -1) {
      removeSession('teacherAddFaceIn/index');
    }
    next();
  },
  created() {
    this.init();
    const cache = getSession('teacherAddFaceIn/index');
    if (cache) {
      try {
        const cachedObj = JSON.parse(cache);
        this.searchObj = Object.assign({}, this.searchObj, cachedObj);
      } catch (e) {}
      if (!this.searchObj.selectedGrade) {
        this.searchObj.selectedGrade = 'ALL';
      }
      if (this.searchObj.searchFaceStatus === undefined) {
        this.searchObj.searchFaceStatus = 'all';
      }
      if (!this.searchObj.sortType) {
        this.searchObj.sortType = 'pinyin';
      }
      if (this.searchObj.classCode) {
        this.getList();
      }
    }
  },
  methods: {
    // 打开班级选择器并精确定位到当前选中项
    openClassPicker() {
      this.tempGrade = this.searchObj.selectedGrade || 'ALL';
      this.classShowPicker = true;
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.classPicker && typeof this.$refs.classPicker.setColumnIndex === 'function') {
            this.$refs.classPicker.setColumnIndex(0, this.currentClassIndex);
          }
        });
      });
    },

    // 点击年级胶囊切换滚轮班级
    handleGradePillClick(gradeCode) {
      if (this.tempGrade === gradeCode) return;
      this.tempGrade = gradeCode;
      this.$nextTick(() => {
        this.$nextTick(() => {
          if (this.$refs.classPicker && typeof this.$refs.classPicker.setColumnIndex === 'function') {
            let targetIdx = 0;
            if (this.searchObj.classCode) {
              const foundIdx = this.filteredClassList.findIndex(item => item.value === this.searchObj.classCode);
              if (foundIdx >= 0) {
                targetIdx = foundIdx;
              }
            }
            this.$refs.classPicker.setColumnIndex(0, targetIdx);
          }
        });
      });
    },

    // 取消或遮罩关闭弹窗：还原 tempGrade
    onCancelPicker() {
      this.classShowPicker = false;
      this.tempGrade = this.searchObj.selectedGrade || 'ALL';
    },

    // 监听 picker 滚动变更
    onPickerChange(picker, value, index) {},

    // 确认选择班级（通过 ref 获取 picker 实例）
    onConfirmPicker() {
      if (this.$refs.classPicker) {
        // 先调用 confirm 强制停止惯性滚动并校准 currentIndex（不绑定 @confirm，杜绝递归爆栈）
        if (typeof this.$refs.classPicker.confirm === 'function') {
          this.$refs.classPicker.confirm();
        }
        let val = null;
        if (typeof this.$refs.classPicker.getValues === 'function') {
          const vals = this.$refs.classPicker.getValues();
          val = vals && vals[0];
        }
        if (!val && typeof this.$refs.classPicker.getColumnIndex === 'function') {
          const cIdx = this.$refs.classPicker.getColumnIndex(0);
          val = this.filteredClassList[cIdx];
        }
        if (!val && this.filteredClassList.length > 0) {
          val = this.filteredClassList[0];
        }
        if (val) {
          // 清空旧班级数据，杜绝新班级异常时残留旧班级人员名单
          this.dataList = [];
          this.indexList = [];
          this.sortedFlatList = [];

          this.searchObj.selectedGrade = this.tempGrade || 'ALL';
          this.searchObj.classCode = val.value;
          this.searchObj.className = val.text;
          // 切换班级后默认重置为首字母排序
          this.searchObj.sortType = 'pinyin';
          this.classShowPicker = false;
          this.getList();
          setSession('teacherAddFaceIn/index', JSON.stringify(this.searchObj));
          return;
        }
      }
      this.classShowPicker = false;
    },

    // 切换状态筛选
    handleFilterChange(val) {
      if (!this.searchObj.classCode) {
        this.$notify({ type: "warning", message: '请先选择班级！' });
        return;
      }
      this.searchObj.searchFaceStatus = val;
      this.dropdownItemChange();
    },

    // 切换排序规则 (首字母排序 / 学号排序)
    handleSortChange(sortType) {
      if (!this.searchObj.classCode) {
        this.$notify({ type: "warning", message: '请先选择班级！' });
        return;
      }
      if (this.searchObj.sortType === sortType) return;
      this.searchObj.sortType = sortType;
      this.dropdownItemChange();
      setSession('teacherAddFaceIn/index', JSON.stringify(this.searchObj));
    },

    // 点击学生卡片 (未制证时去制证，已制证不误跳)
    onCardClick(student) {
      if (+student.faceStatus === 0) {
        this.goInfo(student);
      }
    },

    // 跳转制证页
    goInfo(row) {
      this.$router.push({
        path: '/teacherAddFaceIn/addFaceInfo',
        query: { studentInfo: JSON.stringify(row) }
      });
    },

    // 重置学生制证状态
    handleReset(student) {
      if (!student || !student.snCode) return;
      const confirmFunc = (this.$dialog && this.$dialog.confirm) || Dialog.confirm;
      confirmFunc({
        title: '重置制证确认',
        message: `确定要重置【${student.studentName}（${student.snCode}）】的制证信息吗？`,
        confirmButtonText: '确定重置',
        cancelButtonText: '取消',
        confirmButtonColor: '#ff3b30'
      }).then(() => {
        this.$toast.loading({ message: '正在重置...', forbidClick: true, duration: 0 });
        resetStudentFace({ snCode: student.snCode }).then(res => {
          this.$toast.clear();
          const resData = res && res.data ? res.data : res;
          const isSuccess = resData && (
            resData.status === '00' || 
            resData.status === 200 || 
            resData.status === 0 || 
            resData.code === '00' || 
            resData.code === 200 || 
            resData.code === 0 || 
            resData.success === true
          );
          if (isSuccess) {
            this.$toast.success('重置成功');
            this.getList();
          } else {
            this.$toast.fail((resData && (resData.message || resData.msg)) || '重置失败');
          }
        }).catch(err => {
          this.$toast.clear();
          this.$toast.fail((err && (err.message || err.msg)) || '网络异常，重置失败');
        });
      }).catch(() => {
        // 取消重置
      });
    },

    // 初始化班级列表（去重 + 过滤空 classCode）
    init() {
      getClassInfoList({}).then(res => {
        const rawList = (res && res.data && res.data.data) || [];
        const seen = new Set();
        const uniqueList = [];
        rawList.forEach(item => {
          if (item && item.classCode) {
            const text = `${item.gradeName || ''} ${item.className || ''}`.trim() || item.className;
            const key = `${item.classCode}_${text}`;
            if (!seen.has(key)) {
              seen.add(key);
              const gradeKey = String(item.gradeCode || item.gradeName || 'other');
              uniqueList.push({
                text: text,
                value: item.classCode,
                gradeCode: item.gradeCode,
                gradeName: item.gradeName || '',
                className: item.className || '',
                gradeKey: gradeKey,
                totalCount: Number(item.totalCount) || 0,
                doneCount: Number(item.doneCount) || 0
              });
            }
          }
        });
        this.allClassList = uniqueList;
        // 如果已经加载了当前班级学生，同步更新班级统计
        if (this.searchObj.classCode && this.dataList && this.dataList.length > 0) {
          const curClass = this.allClassList.find(c => c.value === this.searchObj.classCode);
          if (curClass) {
            curClass.totalCount = this.currentStats.total;
            curClass.doneCount = this.currentStats.done;
          }
        }
      }).catch(() => {});
    },

    // 拉取当前班级学生
    getList() {
      if (!this.searchObj.classCode) return;
      this.$toast.loading({ message: '加载中...', forbidClick: true });
      getStudentInfoList({
        classCode: this.searchObj.classCode,
        studentName: this.searchObj.studentName,
      }).then(res => {
        this.dataList = (res && res.data && res.data.data) || [];
        // 同步更新 allClassList 中当前班级的 doneCount 和 totalCount
        if (this.allClassList && this.allClassList.length > 0) {
          const curClass = this.allClassList.find(c => c.value === this.searchObj.classCode);
          if (curClass) {
            curClass.totalCount = this.currentStats.total;
            curClass.doneCount = this.currentStats.done;
          }
        }
        this.dropdownItemChange();
        this.$toast.clear();
      }).catch(() => {
        this.$toast.clear();
      });
    },

    // 本地状态筛选 + 分支排序
    dropdownItemChange() {
      let filteredList = [];
      const status = this.searchObj.searchFaceStatus;

      if (status === 'all') {
        filteredList = [...this.dataList];
      } else if (+status === 0) {
        filteredList = this.dataList.filter(item => +item.faceStatus === 0);
      } else if (+status > 0) {
        filteredList = this.dataList.filter(item => +item.faceStatus > 0);
      }

      if (this.searchObj.sortType === 'snCode') {
        const listCopy = [...filteredList];
        listCopy.sort((a, b) => this.compareSnCode(a, b));
        this.sortedFlatList = listCopy;
      } else {
        this.pinyinSort(filteredList);
      }

      setSession('teacherAddFaceIn/index', JSON.stringify(this.searchObj));
    },

    // 自然学号分段排序算法
    compareSnCode(a, b) {
      const snA = String((a && a.snCode) || '').trim();
      const snB = String((b && b.snCode) || '').trim();
      if (snA === snB) return 0;
      if (!snA) return 1;
      if (!snB) return -1;
      const tokensA = snA.match(/(\d+|\D+)/g) || [];
      const tokensB = snB.match(/(\d+|\D+)/g) || [];
      const len = Math.min(tokensA.length, tokensB.length);
      for (let i = 0; i < len; i++) {
        const tA = tokensA[i];
        const tB = tokensB[i];
        if (tA === tB) continue;
        const isNumA = /^\d+$/.test(tA);
        const isNumB = /^\d+$/.test(tB);
        if (isNumA && isNumB) {
          const cleanA = tA.replace(/^0+/, '') || '0';
          const cleanB = tB.replace(/^0+/, '') || '0';
          if (cleanA.length !== cleanB.length) {
            return cleanA.length - cleanB.length;
          }
          if (cleanA !== cleanB) {
            return cleanA < cleanB ? -1 : 1;
          }
          return tA.length - tB.length;
        }
        const upperA = tA.toUpperCase();
        const upperB = tB.toUpperCase();
        if (upperA !== upperB) {
          return upperA.localeCompare(upperB, 'zh-CN');
        }
        return tA.localeCompare(tB, 'zh-CN');
      }
      return tokensA.length - tokensB.length;
    },

    // 拼音分组逻辑 (A-Z 分组)
    pinyinSort(list) {
      if (!list || list.length === 0) {
        this.indexList = [];
        return;
      }

      const map = {};
      const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ#'.split('');

      letters.forEach(l => map[l] = { title: l, datas: [] });

      list.forEach(item => {
        let py = '';
        try {
          const pyResult = pinyin(item.studentName || '', { 
            mode: 'normal', 
            toneType: 'none', 
            separator: '' 
          });
          py = Array.isArray(pyResult) ? pyResult.join('') : pyResult;
        } catch (e) {
          py = item.studentName;
        }

        let firstChar = '#';
        if (py && py.length > 0) {
          const char = py.charAt(0).toUpperCase();
          if (/^[A-Z]$/.test(char)) {
            firstChar = char;
          }
        }
        
        item._pinyin = py || ''; 
        if (map[firstChar]) {
          map[firstChar].datas.push(item);
        } else {
          map['#'].datas.push(item);
        }
      });

      const turn = [];
      for (const letter of letters) {
        if (map[letter].datas.length > 0) {
          map[letter].datas.sort((a, b) => a._pinyin.localeCompare(b._pinyin, 'zh-CN'));
          turn.push(map[letter]);
        }
      }
      this.indexList = turn;
    }
  }
};
</script>

<style lang="less" scoped>
/* ---------------- 全局容器 ---------------- */
.ios-glass-page {
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at top left, #f1f3f9, #dfe4ea);
  font-family: -apple-system, BlinkMacSystemFont, "PingFang SC", Helvetica, Arial, sans-serif;
  color: #1c1c1e;
  overflow: hidden; /* 禁止页面整体滚动，只让列表区域滚动 */
  display: flex;
  flex-direction: column;
}

/* 动态背景 */
.background-blobs {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 0; pointer-events: none;
  .blob { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.5; }
  .blob-1 { width: 250px; height: 250px; background: #cce0ff; top: -50px; left: -50px; }
  .blob-2 { width: 300px; height: 300px; background: #ffe5d0; top: 20%; right: -80px; }
  .blob-3 { width: 200px; height: 200px; background: #e0f2f1; bottom: 10%; left: 10%; }
}

/* ---------------- 顶部固定区域 ---------------- */
.fixed-header {
  position: relative; z-index: 10;
  padding: 16px 16px 10px;
  flex-shrink: 0;
}

/* 班级选择器 */
.class-selector {
  margin-bottom: 10px;
}
.glass-panel {
  background: rgba(255, 255, 255, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 16px;
  padding: 14px 16px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.03);
  
  &:active { opacity: 0.8; transform: scale(0.99); transition: all 0.2s; }

  .selector-content {
    display: flex; justify-content: space-between; align-items: center;

    .label {
      font-size: 14px;
      color: #8e8e93;
    }

    .value { font-size: 15px; font-weight: 600; color: #007AFF; display: flex; align-items: center; gap: 4px; }
  }
}

/* 控制栏：状态筛选与排序切换 (并列分段控制器) */
.filter-sort-controls {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* 分段控制器 (Segment Control) */
.segment-control {
  .segment-bg {
    display: flex;
    background: rgba(118, 118, 128, 0.12);
    padding: 2px;
    border-radius: 9px;
    
    .segment-item {
      flex: 1; text-align: center;
      padding: 6px 0;
      font-size: 13px; font-weight: 500;
      color: #1c1c1e;
      border-radius: 7px;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
      cursor: pointer;
      
      /* 选中态：白色悬浮卡片 */
      &.active {
        background: #fff;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        font-weight: 600;
        color: #000;
      }
    }
  }
}

/* ---------------- 列表区域 ---------------- */
.list-container {
  position: relative; z-index: 1;
  flex: 1;
  overflow-y: auto; /* 内部滚动 */
  padding-bottom: 20px;
  
  /* 隐藏滚动条美观 */
  &::-webkit-scrollbar { width: 0; }
}

/* 学号排序平铺容器 */
.flat-list-wrapper {
  padding-top: 4px;
}

/* 序号徽章 */
.index-badge {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: rgba(0, 122, 255, 0.08);
  color: #007AFF;
  font-size: 12px;
  font-weight: 600;
  font-family: 'DIN Alternate', -apple-system, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
}

/* 锚点样式重写 */
::v-deep .van-index-anchor {
  background: transparent !important;
  color: #8e8e93;
  font-weight: 600;
  padding-left: 20px;
  line-height: 32px;
}

/* 右侧索引栏样式重写 */
::v-deep .van-index-bar__sidebar {
  right: 8px;
  .van-index-bar__index {
    color: #007AFF;
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    padding: 2px 4px;
  }
}

/* 学生卡片 */
.student-card {
  display: flex; 
  align-items: center;
  background: rgba(255, 255, 255, 0.65);
  margin: 0 16px 12px 16px;
  padding: 12px 14px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 12px rgba(0,0,0,0.03);
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
  position: relative;
  overflow: hidden;

  /* 点击时的反馈效果 */
  &:active { 
    transform: scale(0.985); 
    background: rgba(255, 255, 255, 0.85);
  }

  /* 中间信息 */
  .info {
    flex: 1;
    display: flex; flex-direction: column; justify-content: center;
    
    .name-row {
      margin-bottom: 4px;
      .name { 
        font-size: 16px; font-weight: 600; color: #1c1c1e; letter-spacing: 0.5px;
      }
    }
    
    .meta-row {
      font-size: 13px; color: #8e8e93; display: flex; align-items: center;
      .label { margin-right: 6px; font-size: 11px; background: rgba(0,0,0,0.05); padding: 1px 4px; border-radius: 4px;}
      .code { font-family: 'DIN Alternate', sans-serif; letter-spacing: 0.5px; }
    }
  }

  /* 右侧操作区 */
  .action-area {
    margin-left: 10px;
    flex-shrink: 0;

    /* 已制证操作组 (状态展示 + 重置按钮并排) */
    .action-done-group {
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    /* 状态A: 已制证 (静态展示) */
    .status-box.done {
      display: flex; flex-direction: column; align-items: center;
      
      .icon-circle {
        width: 22px; height: 22px; border-radius: 50%;
        background: #34C759; color: #fff;
        display: flex; align-items: center; justify-content: center;
        margin-bottom: 2px;
        box-shadow: 0 2px 5px rgba(52, 199, 89, 0.3);
        i { font-size: 12px; }
      }
      .text { font-size: 10px; color: #34C759; font-weight: 500; }
    }

    /* 重置按钮 (柔和警示红轻拟态圆角胶囊) */
    .reset-btn {
      background: rgba(255, 59, 48, 0.08);
      color: #ff3b30;
      border: 1px solid rgba(255, 59, 48, 0.22);
      border-radius: 16px;
      padding: 5px 12px;
      font-size: 12px;
      font-weight: 500;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
      box-shadow: 0 2px 5px rgba(255, 59, 48, 0.06);

      &:active {
        background: rgba(255, 59, 48, 0.18);
        transform: scale(0.95);
      }
    }

    /* 状态B: 去制证 (按钮样式) */
    .status-btn.todo {
      background: #007AFF;
      color: #fff;
      padding: 8px 14px;
      border-radius: 20px;
      font-size: 13px; font-weight: 500;
      display: flex; align-items: center; gap: 4px;
      box-shadow: 0 4px 10px rgba(0, 122, 255, 0.25);
      transition: all 0.2s;
      cursor: pointer;
      
      &:active {
        background: #0062cc;
        transform: scale(0.96);
      }

      /* 按钮内图标微调 */
      i { font-size: 14px; }
    }
  }
}

/* 空状态 */
.empty-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding-top: 100px; color: #8e8e93; font-size: 14px;
  p { margin-top: 10px; }
}

/* 底部弹窗 */
.glass-popup { background: #f2f2f7; }
.popup-header {
  display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; background: #fff;
  .btn { font-size: 15px; cursor: pointer; }
  .cancel { color: #8e8e93; }
  .confirm { color: #007AFF; font-weight: 600; }
  .title { font-weight: 600; font-size: 16px; }
}

/* 年级快捷胶囊栏 */
.grade-capsule-bar {
  padding: 10px 16px 8px;
  background: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .grade-scroll-wrap {
    display: flex;
    overflow-x: auto;
    gap: 8px;
    padding-bottom: 2px;
    
    /* 隐藏滚动条 */
    &::-webkit-scrollbar { display: none; width: 0; height: 0; }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .grade-pill {
    flex-shrink: 0;
    padding: 6px 14px;
    border-radius: 16px;
    font-size: 13px;
    background: rgba(118, 118, 128, 0.08);
    color: #636366;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.25, 0.8, 0.25, 1);
    font-weight: 500;

    .grade-count {
      font-size: 11px;
      opacity: 0.75;
    }

    &.active {
      background: #007AFF;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(0, 122, 255, 0.3);

      .grade-count {
        opacity: 0.9;
      }
    }

    &:active {
      transform: scale(0.96);
    }
  }
}

/* 班级滚轮内自定义行与徽章 */
.picker-option-row {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;

  .picker-class-name {
    font-size: 15px;
    color: #1c1c1e;
    font-weight: 500;
    max-width: 65%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-right: 8px;
  }

  .picker-count-badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
    background: rgba(0, 122, 255, 0.1);
    color: #007AFF;
    border: 1px solid rgba(0, 122, 255, 0.22);
    line-height: 1.3;
    white-space: nowrap;

    &.is-full {
      background: rgba(52, 199, 89, 0.12);
      color: #34C759;
      border: 1px solid rgba(52, 199, 89, 0.28);
    }
  }
}

::v-deep .van-picker-column__item--selected {
  .picker-class-name {
    font-weight: 600;
    color: #000;
  }
}
</style>
